/**
 * OCR Uploader Component
 * Allows users to upload documents for Optical Character Recognition
 * Compatible with Node.js 16.20.2
 */

import React, { useRef, useState } from 'react';
import { processOCRFile } from '../services/ocrApi';
import '../styles/OCRUploader.css';

function OCRUploader({ onTranscriptReceived, isEnabled }) {
  const fileInputRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const SUPPORTED_FORMATS = ['.jpeg', '.jpg', '.png', '.pdf', '.tiff'];
  const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200MB

  /**
   * Handle file selection
   */
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      validateAndProcessFile(file);
    }
  };

  /**
   * Validate file before processing
   */
  const validateAndProcessFile = (file) => {
    setError(null);

    // Check file extension
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    if (!SUPPORTED_FORMATS.includes(fileExtension)) {
      setError(
        `Unsupported file format. Supported formats: ${SUPPORTED_FORMATS.join(', ')}`
      );
      return;
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      setError('File size exceeds 200MB limit');
      return;
    }

    setSelectedFile(file);
    processFile(file);
  };

  /**
   * Process the selected file with OCR API
   */
  const processFile = async (file) => {
    setIsProcessing(true);
    setProgress(0);
    setProgressMessage('Initializing...');
    setError(null);

    console.log('Starting OCR processing for file:', file.name);

    try {
      const result = await processOCRFile(file, (progressData) => {
        console.log('Progress:', progressData);
        setProgress(progressData.percentage);
        setProgressMessage(progressData.status);
      });

      console.log('OCR result received:', result);

      // Send results to parent component
      onTranscriptReceived({
        text: result.text,
        metadata: {
          source: 'OCR',
          fileName: result.fileName,
          fileSize: result.fileSize,
          uploadedAt: result.uploadedAt,
          jobId: result.jobId,
          tablesExtracted: result.extractedTables?.length || 0,
          partialSuccess: result.partialSuccess,
          processingDetails: result.processingDetails,
          downloadError: result.metadata?.downloadError
        }
      });

      setProgress(100);
      setProgressMessage('OCR processing complete!');

      // Reset after 2 seconds
      setTimeout(() => {
        setIsProcessing(false);
        setProgress(0);
        setProgressMessage('');
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }, 2000);
    } catch (err) {
      console.error('OCR Error:', err);
      setError(err.message || 'Failed to process file. Please try again.');
      setIsProcessing(false);
      setProgress(0);
      setProgressMessage('');
    }
  };

  /**
   * Handle browse button click
   */
  const handleBrowseClick = () => {
    if (!isProcessing && isEnabled) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className="ocr-uploader">
      <div className="ocr-header">
        <h3>🖼️ OCR Upload</h3>
        <p>Upload documents to extract text instantly</p>
      </div>

      <div className="ocr-content">
        {!isProcessing ? (
          <div className="ocr-upload-area">
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              accept=".jpeg,.jpg,.png,.pdf,.tiff"
              style={{ display: 'none' }}
              disabled={isProcessing || !isEnabled}
            />

            <div
              className="upload-box"
              onClick={handleBrowseClick}
              style={{
                opacity: isEnabled ? 1 : 0.6,
                cursor: isEnabled && !isProcessing ? 'pointer' : 'not-allowed'
              }}
            >
              <div className="upload-icon">📁</div>
              <p className="upload-text">Click to browse or drag & drop</p>
              <p className="upload-hint">
                Supported: JPEG, JPG, PNG, PDF, TIFF (Max 200MB)
              </p>
            </div>

            {selectedFile && (
              <div className="selected-file">
                <p>
                  <strong>Selected:</strong> {selectedFile.name}
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="processing-container">
            <div className="spinner"></div>
            <p className="processing-text">{progressMessage}</p>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <p className="progress-percentage">{Math.round(progress)}%</p>
          </div>
        )}

        {error && (
          <div className="ocr-error">
            <p>
              <strong>⚠️ Error:</strong> {error}
            </p>
          </div>
        )}

        {!isEnabled && (
          <div className="disabled-message">
            <p>⏸️ Speech-to-text is in progress. Please wait.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default OCRUploader;
